import React, { useState } from "react";
import {
  HiUser,
  HiShieldCheck,
  HiArrowUturnLeft,
  HiPlus,
  HiSignalSlash,
  HiCircleStack,
  HiTrash,
  HiEye,
  HiSquares2X2,
} from "react-icons/hi2";
import { HiX } from "react-icons/hi";
import {
  useFeedbackManagement,
  useAppUpdates,
  useFeedbackHelpers,
  FeedbackItem,
} from "../../hooks/useFeedbackData";

const FeedbackManagement: React.FC = () => {
  // Use offline-first hooks following developer guidelines
  const {
    feedbackList,
    respondToFeedback,
    deleteFeedback,
    unrespondedCount,
    loading: feedbackLoading,
    error: feedbackError,
    isFromCache: feedbackFromCache,
    isOffline: feedbackOffline,
  } = useFeedbackManagement();

  const {
    updates,
    addUpdate,
    loading: updatesLoading,
    isFromCache: updatesFromCache,
    isOffline: updatesOffline,
  } = useAppUpdates();

  const { formatDate } = useFeedbackHelpers();

  // Local state for UI interactions
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackItem | null>(
    null
  );
  const [replyText, setReplyText] = useState("");
  const [submittingReply, setSubmittingReply] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [newUpdateText, setNewUpdateText] = useState("");
  const [submittingUpdate, setSubmittingUpdate] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [feedbackToDelete, setFeedbackToDelete] = useState<FeedbackItem | null>(
    null
  );
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleReplyClick = (feedback: FeedbackItem) => {
    setSelectedFeedback(feedback);
    setReplyText(feedback.adminResponse || "");
    setReplyError(null); // Clear any previous errors
    setReplyDialogOpen(true);
  };

  const handleReplySubmit = async () => {
    if (!selectedFeedback || !replyText.trim()) return;

    setSubmittingReply(true);
    setReplyError(null); // Clear any previous errors
    try {
      await respondToFeedback(selectedFeedback.id, replyText.trim());
      setReplyDialogOpen(false);
      setSelectedFeedback(null);
      setReplyText("");
      setReplyError(null);
    } catch (error) {
      console.error("Error submitting reply:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to submit reply. Please try again.";
      setReplyError(errorMessage);
    }
    setSubmittingReply(false);
  };

  const handleAddUpdate = async () => {
    if (!newUpdateText.trim()) return;

    setSubmittingUpdate(true);
    setUpdateError(null); // Clear any previous errors
    try {
      await addUpdate(newUpdateText.trim());
      setUpdateDialogOpen(false);
      setNewUpdateText("");
      setUpdateError(null);
    } catch (error) {
      console.error("Error adding update:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to add update. Please try again.";
      setUpdateError(errorMessage);
    }
    setSubmittingUpdate(false);
  };

  const handleDeleteClick = (feedback: FeedbackItem) => {
    setFeedbackToDelete(feedback);
    setDeleteError(null); // Clear any previous errors
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!feedbackToDelete) return;

    setDeleting(true);
    setDeleteError(null); // Clear any previous errors
    try {
      await deleteFeedback(feedbackToDelete.id);
      setDeleteDialogOpen(false);
      setFeedbackToDelete(null);
      setDeleteError(null);
    } catch (error) {
      console.error("Error deleting feedback:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to delete feedback. Please try again.";
      setDeleteError(errorMessage);
    }
    setDeleting(false);
  };

  // Show loading state for initial load
  if (
    feedbackLoading &&
    !feedbackList.length &&
    updatesLoading &&
    !updates.length
  ) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center">
          <div className="loading loading-spinner loading-lg"></div>
          <p className="text-sm text-base-content/70 mt-4">Loading feedback data...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-semibold">Feedback Management</h1>
        <button
          className="btn btn-primary gap-2"
          onClick={() => {
            setUpdateError(null);
            setUpdateDialogOpen(true);
          }}
        >
          <HiPlus className="w-4 h-4" />
          Add Update
        </button>
      </div>

      {/* Offline/Cache Status Indicators */}
      {(feedbackOffline || updatesOffline) && (
        <div className="alert alert-warning mb-4">
          <HiSignalSlash className="w-5 h-5" />
          <span>
            You're currently offline. Changes will be synced when you're back
            online.
          </span>
        </div>
      )}

      {(feedbackFromCache || updatesFromCache) &&
        !(feedbackOffline || updatesOffline) && (
          <div className="alert alert-info mb-4">
            <HiCircleStack className="w-5 h-5" />
            <span>
              Showing cached data.{" "}
              {feedbackLoading || updatesLoading
                ? "Updating..."
                : "Data is up to date."}
            </span>
          </div>
        )}

      {/* Error Display */}
      {feedbackError && (
        <div className="alert alert-error mb-4">
          <span>Error loading feedback: {feedbackError.message}</span>
        </div>
      )}

      {/* Stats */}
      <div className="flex gap-4 mb-6">
        <div className="badge badge-primary badge-outline badge-lg">
          {feedbackList.length} Total Submissions
        </div>
        <div
          className={`badge badge-outline badge-lg ${
            unrespondedCount > 0 ? "badge-error" : "badge-success"
          }`}
        >
          {unrespondedCount} Awaiting Response
        </div>
        {feedbackLoading && (
          <div className="badge badge-outline gap-2">
            <span className="loading loading-spinner loading-xs"></span>
            Syncing...
          </div>
        )}
      </div>

      {/* Latest Updates Section */}
      <div className="card bg-base-100 shadow-sm mb-6">
        <div className="card-body">
          <div className="mb-4">
            <h2 className="text-lg font-semibold">
              Latest Updates ({updates.length})
            </h2>
            {updatesLoading && (
              <div className="loading loading-spinner loading-sm"></div>
            )}
          </div>
          {updates.length > 0 ? (
            <div className="space-y-2">
              {updates.slice(0, 1).map((update) => (
                <div key={update.id} className="">
                  <span className="text-xs text-base-content/60 min-w-[120px] mt-0.5">
                    {formatDate(update.createdAt)}
                  </span>
                  <p className="text-sm whitespace-pre-line">
                    {update.message}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-base-content/70">No updates posted yet.</p>
          )}
        </div>
      </div>

      {/* Feedback List */}
      <h2 className="text-lg font-semibold mb-4">User Feedback</h2>

      <div className="space-y-4">
        {feedbackList.map((item) => (
          <div key={item.id} className="card bg-base-100 shadow-sm ">
            <div className="card-body">
              {/* Header with user info and actions */}
              <div className="flex justify-between items-start mb-4">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <div className="avatar">
                      <div className="w-6 h-6 rounded-full bg-base-content/40 !flex !items-center !justify-center">
                        <HiUser className="w-3 h-3 text-white" />
                      </div>
                    </div>
                    <span className="text-xs text-base-content/60">
                      {formatDate(item.createdAt)}
                    </span>
                    {!item.adminResponse && (
                      <div className="px-3 py-1 rounded-2xl !bg-orange-500/15 !border !border-orange-500/30 text-xs text-orange-600 dark:text-orange-400">
                        Awaiting response
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-base-content/70">User ID:</span>
                    <code className="text-xs bg-base-200 px-2 py-1 rounded">
                      {item.userId}
                    </code>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() =>
                      window.open(`/feed/?uid=${item.userId}`, "_blank")
                    }
                    className="btn btn-sm btn-ghost text-primary hover:bg-primary hover:text-white"
                    title="View user's feed"
                  >
                    <HiEye className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() =>
                      window.open(`/dashboard/?uid=${item.userId}`, "_blank")
                    }
                    className="btn btn-sm btn-ghost text-info hover:bg-info hover:text-white"
                    title="View user's dashboard"
                  >
                    <HiSquares2X2 className="w-3 h-3" />
                  </button>
                  <button
                    className="btn btn-sm !bg-base-300 gap-2"
                    onClick={() => handleReplyClick(item)}
                  >
                    <HiArrowUturnLeft className="w-3 h-3" />
                    {item.adminResponse ? "Edit Reply" : "Reply"}
                  </button>
                  <button
                    className="btn btn-sm !bg-red-600 !text-white gap-2"
                    onClick={() => handleDeleteClick(item)}
                  >
                    <HiTrash className="w-3 h-3" />
                    Delete
                  </button>
                </div>
              </div>

              {/* Full Feedback Text */}
              <div className="mb-4">
                <p className="text-sm whitespace-pre-line">{item.feedback}</p>
              </div>

              {/* Attached Image - Always show if exists */}
              {item.imageUrl && (
                <div className="mb-4">
                  <span className="text-xs text-base-content/60 block mb-2">
                    Attached Image:
                  </span>
                  <img
                    src={item.imageUrl}
                    alt="Feedback screenshot"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                    className="max-w-[400px] max-h-[300px] rounded-lg border border-base-300 cursor-pointer hover:opacity-90"
                    onClick={() => window.open(item.imageUrl, "_blank")}
                  />
                </div>
              )}

              {/* Admin Response - Always show if exists */}
              {item.adminResponse && (
                <>
                  <div className="divider"></div>
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="avatar">
                        <div className="w-6 h-6 rounded-full bg-primary !flex items-center justify-center">
                          <HiShieldCheck className="w-3 h-3 text-white" />
                        </div>
                      </div>
                      <span className="text-xs font-semibold text-primary">
                        Your Response
                      </span>
                      {item.adminResponseAt && (
                        <span className="text-xs text-base-content/60">
                          {formatDate(item.adminResponseAt)}
                        </span>
                      )}
                    </div>
                    <div className="bg-blue-500/10 border border-primary rounded-lg p-4">
                      <p className="text-sm text-primary font-medium whitespace-pre-line">
                        {item.adminResponse}
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        ))}

        {feedbackList.length === 0 && !feedbackLoading && (
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body text-center">
              <p className="text-sm text-base-content/70">
                No feedback submissions yet.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Reply Dialog */}
      {replyDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-base-100 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-base-300">
              <h3 className="text-lg font-semibold">
                {selectedFeedback?.adminResponse
                  ? "Edit Reply"
                  : "Reply to Feedback"}
              </h3>
              <button
                onClick={() => setReplyDialogOpen(false)}
                className="btn !btn-ghost btn-sm btn-square"
              >
                <HiX className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto">
              {replyError && (
                <div className="alert alert-error mb-4">
                  <span>{replyError}</span>
                </div>
              )}
              {selectedFeedback && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-base-content/80 mb-2">
                    Original Feedback:
                  </h4>
                  <div className="bg-base-200 p-4 rounded-lg">
                    <p className="text-sm">{selectedFeedback.feedback}</p>
                  </div>
                </div>
              )}
              <div className="form-group flex flex-col gap-2">
                <label className="form-label">Your Response</label>
                <textarea
                  className="textarea textarea-bordered w-full h-32"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Enter your response to this feedback..."
                  disabled={submittingReply}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-base-300">
              <button
                onClick={() => {
                  setReplyDialogOpen(false);
                  setReplyError(null);
                }}
                className="btn !btn-ghost"
                disabled={submittingReply}
              >
                Cancel
              </button>
              <button
                onClick={handleReplySubmit}
                className="btn btn-primary"
                disabled={!replyText.trim() || submittingReply}
              >
                {submittingReply ? (
                  <>
                    <span className="loading loading-spinner loading-sm mr-2"></span>
                    Submitting...
                  </>
                ) : (
                  "Submit Reply"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Update Dialog */}
      {updateDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-base-100 rounded-lg shadow-xl max-w-lg w-full">
            <div className="flex items-center justify-between p-6 border-b border-base-300">
              <h3 className="text-lg font-grotesk">Add New Update </h3>
              <button onClick={() => setUpdateDialogOpen(false)} className="">
                <HiX className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6">
              {updateError && (
                <div className="alert alert-error mb-4">
                  <span>{updateError}</span>
                </div>
              )}
              <div className="form-group">
                <textarea
                  className="form-textarea h-48 w-full bg-base-300 rounded-md p-4"
                  value={newUpdateText}
                  onChange={(e) => setNewUpdateText(e.target.value)}
                  placeholder="Enter the update message that will appear in the green updates section..."
                  disabled={submittingUpdate}
                />
              </div>
            </div>
            <div className="flex justify-between gap-3 p-6 border-t border-base-300">
              <button
                onClick={() => {
                  setUpdateDialogOpen(false);
                  setUpdateError(null);
                }}
                className="bg-base-200 p-2 px-4 rounded-md font-grotesk"
                disabled={submittingUpdate}
              >
                Cancel
              </button>
              <button
                onClick={handleAddUpdate}
                className="bg-neutral text-white p-2 px-4 rounded-md font-grotesk"
                disabled={!newUpdateText.trim() || submittingUpdate}
              >
                {submittingUpdate ? (
                  <>
                    <span className="loading loading-spinner loading-sm mr-2"></span>
                    Adding...
                  </>
                ) : (
                  "Add Update"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-base-100 rounded-lg shadow-xl max-w-lg w-full">
            <div className="flex items-center justify-between p-6 border-b border-base-300">
              <h3 className="text-lg font-semibold">Delete Feedback</h3>
              <button
                onClick={() => setDeleteDialogOpen(false)}
                className="btn !btn-ghost btn-sm btn-square"
              >
                <HiX className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6">
              {deleteError && (
                <div className="alert alert-error mb-4">
                  <span>{deleteError}</span>
                </div>
              )}
              {feedbackToDelete && (
                <div>
                  <p className="text-sm mb-4">
                    Are you sure you want to delete this feedback? This action
                    cannot be undone.
                  </p>
                  <p className="text-xs text-base-content/60 mb-2">
                    From: {feedbackToDelete.userId}
                  </p>
                  <div className="bg-base-200 p-4 rounded-lg max-h-[200px] overflow-auto">
                    <p className="text-sm">{feedbackToDelete.feedback}</p>
                  </div>
                  {feedbackToDelete.imageUrl && (
                    <div className="mt-4">
                      <span className="text-xs text-base-content/60 block mb-2">
                        Attached Image:
                      </span>
                      <img
                        src={feedbackToDelete.imageUrl}
                        alt="Feedback screenshot"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                        className="max-w-[200px] max-h-[150px] rounded-lg border border-base-300"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-base-300">
              <button
                onClick={() => {
                  setDeleteDialogOpen(false);
                  setDeleteError(null);
                }}
                className="btn !btn-ghost"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="btn !bg-red-700 !text-white"
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <span className="loading loading-spinner loading-sm mr-2"></span>
                    Deleting...
                  </>
                ) : (
                  "Delete Feedback"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackManagement;
